"use client";

import { useEffect } from "react";

type ToolRegistration = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: {
    readOnlyHint?: boolean;
    consequentialHint?: boolean;
    untrustedContentHint?: boolean;
  };
  execute: (input: Record<string, unknown>) => Promise<string> | string;
};

type ModelContext = {
  registerTool: (tool: ToolRegistration) => Promise<void> | void;
  unregisterTool?: (name: string) => Promise<void> | void;
};

function getModelContext(): ModelContext | null {
  if (typeof document === "undefined") return null;
  const doc = document as Document & { modelContext?: ModelContext };
  const nav = typeof navigator !== "undefined"
    ? (navigator as Navigator & { modelContext?: ModelContext })
    : null;
  return doc.modelContext ?? nav?.modelContext ?? null;
}

type Props = {
  site: "scaffolder" | "customer";
};

/**
 * Registers imperative WebMCP tools when the browser exposes modelContext
 * (Chrome WebMCP / agentic browsing). No-ops where unsupported.
 */
export function WebMcpSiteTools({ site }: Props) {
  useEffect(() => {
    const ctx = getModelContext();
    if (!ctx?.registerTool) return;

    const tools: ToolRegistration[] =
      site === "scaffolder"
        ? [
            {
              name: "start_free_trial",
              description: "Open the ScaffOps signup page to start a free trial.",
              inputSchema: { type: "object", properties: {} },
              annotations: { readOnlyHint: true, consequentialHint: false },
              execute: () => {
                window.location.assign("/signup");
                return "Opening ScaffOps signup.";
              },
            },
            {
              name: "view_pricing",
              description: "Open ScaffOps pricing (Lite, Studio, Command).",
              inputSchema: { type: "object", properties: {} },
              annotations: { readOnlyHint: true },
              execute: () => {
                window.location.assign("/pricing");
                return "Opening pricing.";
              },
            },
            {
              name: "open_switch_offer",
              description:
                "Open the ScaffOps switching offer (up to £1,200 credit / 6 months free on any plan).",
              inputSchema: { type: "object", properties: {} },
              annotations: { readOnlyHint: true },
              execute: () => {
                window.location.assign("/switch");
                return "Opening switch offer.";
              },
            },
            {
              name: "open_contact",
              description: "Open the ScaffOps contact form for sales or support.",
              inputSchema: { type: "object", properties: {} },
              annotations: { readOnlyHint: true },
              execute: () => {
                window.location.assign("/contact");
                return "Opening contact.";
              },
            },
          ]
        : [
            {
              name: "find_local_scaffolders",
              description: "Search verified scaffolding companies near a UK postcode.",
              inputSchema: {
                type: "object",
                properties: {
                  postcode: {
                    type: "string",
                    description: "UK postcode",
                  },
                },
                required: ["postcode"],
              },
              annotations: { readOnlyHint: true },
              execute: (input) => {
                const pc = String(input.postcode ?? "").trim().toUpperCase();
                const q = pc
                  ? `/scaffolders?postcode=${encodeURIComponent(pc)}&miles=30`
                  : "/scaffolders";
                window.location.assign(q);
                return `Searching scaffolders${pc ? ` near ${pc}` : ""}.`;
              },
            },
            {
              name: "get_scaffold_quote",
              description: "Start requesting scaffolding quotes for a UK postcode.",
              inputSchema: {
                type: "object",
                properties: {
                  postcode: { type: "string", description: "UK postcode for the site" },
                },
              },
              annotations: { readOnlyHint: true, consequentialHint: false },
              execute: (input) => {
                const pc = String(input.postcode ?? "").trim().toUpperCase();
                const q = pc
                  ? `/post-job?postcode=${encodeURIComponent(pc)}`
                  : "/post-job";
                window.location.assign(q);
                return "Opening quote request.";
              },
            },
            {
              name: "post_scaffold_job",
              description: "Open the post-job wizard to request scaffolding quotes.",
              inputSchema: { type: "object", properties: {} },
              annotations: { readOnlyHint: true },
              execute: () => {
                window.location.assign("/post-job");
                return "Opening post-job wizard.";
              },
            },
          ];

    const names = tools.map((t) => t.name);
    let cancelled = false;

    void (async () => {
      for (const tool of tools) {
        if (cancelled) return;
        try {
          await ctx.registerTool(tool);
        } catch {
          // Browser may reject duplicate names or incomplete origin trials.
        }
      }
    })();

    return () => {
      cancelled = true;
      if (!ctx.unregisterTool) return;
      for (const name of names) {
        try {
          void ctx.unregisterTool?.(name);
        } catch {
          /* ignore */
        }
      }
    };
  }, [site]);

  return null;
}
